import { Lead } from '../types/lead';
import { LeadCard } from './LeadCard';
import { CheckCircle, Search, Filter, TrendingUp } from 'lucide-react';
import { useState } from 'react';
import { Alert, AlertDescription } from './ui/alert';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { UpdateLeadStatusModal } from './UpdateLeadStatus';
import { useDispatch } from 'react-redux';
import { applyLead } from 'slices/thunk';
import { CallAPIGetPromise, CallAPIPostPromise } from 'Common/commaon';
import { getDecodeId } from 'slices/userProfile/thunk';
import { toast } from 'react-toastify';

interface AcceptedLeadsProps {
  leads: any[];
  onViewDetails: (leadId: string) => void;
  onUpdateStatus?: () => void;
  onDeclineLead?: (lead: any) => void;
  canRespondToJobs?: boolean;
  isLoading?: boolean;
}

export function AcceptedLeads({ 
  leads, 
  onViewDetails, 
  onUpdateStatus, 
  onDeclineLead, 
  canRespondToJobs = false,
  isLoading = false 
}: AcceptedLeadsProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedLeadForUpdate, setSelectedLeadForUpdate] = useState<Lead | null>(null);

  // Filter leads
  const filteredLeads = leads.filter(lead => {
    const matchesSearch = 
      lead.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.location.city.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesCategory = categoryFilter === 'all' || lead.category === categoryFilter;
    
    return matchesSearch && matchesCategory;
  });

  // Sort leads
  const sortedLeads = [...filteredLeads].sort((a, b) => {
    switch (sortBy) {
      case 'newest':
        return new Date(b.acceptedDate || b.postedDate).getTime() - new Date(a.acceptedDate || a.postedDate).getTime();
      case 'oldest':
        return new Date(a.acceptedDate || a.postedDate).getTime() - new Date(b.acceptedDate || b.postedDate).getTime();
      case 'budget-high':
        return Number(b.budget)  - Number(a.budget) ;
      case 'budget-low':
        return a.budget - b.budget;
      default:
        return 0;
    }
  });

  const categories = ['all', ...Array.from(new Set(leads.map(lead => lead.category)))];

  const handleUpdateStatus = (status: 'completed' | 'dispute', ) => {
    if (selectedLeadForUpdate) {
       CallAPIPostPromise(`/jobs/updateJob`, {
        status: status==="completed"?"COMPLETED":"DISPUTE",
        contractorId: getDecodeId(),
        jobId: selectedLeadForUpdate.id,
      }).then((res:any)=>{
        
        toast.success("Lead Status Updated")
      }).catch((e:any)=>{
        toast.warning("Something went wrong")

      }).finally(()=>{
        onUpdateStatus?.()
      })


      setSelectedLeadForUpdate(null);
    }
  };

  const totalValue = leads.reduce((sum, lead) => sum + Number(lead.budget), 0);

  return (
    <div className="lead-management-content">
      <div className="">
        {/* Stats */}
        {leads.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-green-100 rounded-lg">
                  <CheckCircle className="size-6 text-green-700" />
                </div>
                <div>
                  <div className="text-muted-foreground">Active Projects</div>
                  <div className="text-2xl">{leads.length}</div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <TrendingUp className="size-6 text-blue-700" />
                </div>
                <div>
                  <div className="text-muted-foreground">Total Value</div>
                  <div className="text-2xl">${totalValue.toLocaleString()}</div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-purple-100 rounded-lg">
                  <Filter className="size-6 text-purple-700" />
                </div>
                <div>
                  <div className="text-muted-foreground">Filtered Results</div>
                  <div className="text-2xl">{sortedLeads.length}</div>
                </div>
              </div>
            </div>
          </div>
        )}

   

        {/* Search and Filters */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6 border border-gray-100">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="app-search-field flex-1">
              <Search className="app-field-icon" />
              <Input
                type="text"
                placeholder="Search accepted leads..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            <div className="flex gap-3">
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="newest">Newest First</SelectItem>
                  <SelectItem value="oldest">Oldest First</SelectItem>
                  <SelectItem value="budget-high">Highest Budget</SelectItem>
                  <SelectItem value="budget-low">Lowest Budget</SelectItem>
                </SelectContent>
              </Select>

              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((category) => (
                    <SelectItem key={category} value={category}>
                      {category === 'all' ? 'All Categories' : category.charAt(0).toUpperCase() + category.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {(searchQuery || categoryFilter !== 'all') && (
            <div className="flex items-center gap-2 mt-4">
              <span className="text-sm text-muted-foreground">Active filters:</span>
              {searchQuery && (
                <Badge variant="secondary" className="gap-1">
                  Search: {searchQuery}
                  <button onClick={() => setSearchQuery('')} className="ml-1 hover:text-destructive">
                    ×
                  </button>
                </Badge>
              )}
              {categoryFilter !== 'all' && (
                <Badge variant="secondary" className="gap-1">
                  Category: {categoryFilter}
                  <button onClick={() => setCategoryFilter('all')} className="ml-1 hover:text-destructive">
                    ×
                  </button>
                </Badge>
              )}
            </div>
          )}
        </div>

        {/* Leads Grid */}
        { sortedLeads.length > 0 ? (
          <div className="grid grid-cols-1 gap-6">
            {sortedLeads.map((lead, index) => (
                <LeadCard
                  lead={lead}
                  onViewDetails={onViewDetails}
                  showActionButtons={true}
                  isAcceptedLead={true}
                  onUpdateStatus={canRespondToJobs ? () => setSelectedLeadForUpdate(lead) : undefined}
                  onDeclineLead={canRespondToJobs && onDeclineLead ? ()=>onDeclineLead(lead) : undefined}
                />
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            {leads.length === 0 ? (
              <div>
                <p className="text-muted-foreground mb-2">
                  You haven't accepted any leads yet.
                </p>
                <p className="text-muted-foreground">
                  Browse available leads in the Leads Pro section to get started.
                </p>
              </div>
            ) : (
              <p className="text-muted-foreground">
                No accepted leads found matching your criteria. Try adjusting your filters.
              </p>
            )}
          </div>
        )}
      </div>

      {/* Update Status Modal */}
      <UpdateLeadStatusModal
        isOpen={canRespondToJobs && selectedLeadForUpdate !== null}
        onClose={() => setSelectedLeadForUpdate(null)}
        onConfirm={handleUpdateStatus}
        leadTitle={selectedLeadForUpdate?.title || ''}
      />
    </div>
  );
}
